import {
    Column,
    LogoOptionType,
    PdfOrientationType,
    ReportSettings,
    RoundingType,
} from '../../models/report-settings.interface';
import { CloneUtils } from '../../utils/clone-utils';
import { SelectOption } from '../../types/angular-primeng-api';
import { DefaultDateFormatListService } from '../default-date-format-list.service';
import { TranslateService } from '@ngx-translate/core';
import { LogoOption } from '@/core/models/logo-option.interface';
import { TimeFormat } from '@/core/models/time-format';

export class ReportSettingDefaults {
    public static readonly defaultPdfHeading: string = 'Report';
    public static readonly defaultRoundingEnabled = false;

    /**
     * A fast method with hard-coded options (manually aligned with the options from DefaultDateFormatListService and so on)
     * This can be used for any user except for the current user where correctness is the most important thing.
     * This method is used in the constructor of the UserSettings object and needs to be fast.
     */
    public static getUserInitSettings(): ReportSettings {
        // Note: This was also a separate logic in the legacy code
        return {
            dateFormat: 'DD.MM.YYYY',
            timeFormat: 'HH:mm',
            tableColumns: [],
            durationFormat: 'HH:mm:ss',
            durationRoundingType: null,
            durationRoundingUnit: null,
            roundingEnabled: this.defaultRoundingEnabled,
            logoOption: null,
            heading: this.defaultPdfHeading,
            pdfOrientation: 'landscape', // TODO this needs to be always aligned with report--customization.component default value
        };
    }

    public static getDefaultSettings(): ReportSettings {
        const defaultTableColumns: Column[] = [];
        for (let i = 0; i < 9; i++) {
            defaultTableColumns.push({
                id: i,
                visible: true,
            });
        }

        /*
            Note: defaults used in legacy code were:
             - durationRoundingType: 'none' and 'up'
             - durationRoundingUnit: 0 and 1
             - heading: Report
         */

        return {
            dateFormat: this.getDefaultDateFormat(),
            timeFormat: this.getDefaultTimeFormat(),
            tableColumns: defaultTableColumns,
            durationFormat: this.getDefaultDurationFormat(),
            durationRoundingType: null, // 'none' may have been intended be used to represent no rounding, it is not used that way, current logic is that null is used when roundingEnabled is false
            durationRoundingUnit: null,
            roundingEnabled: this.defaultRoundingEnabled,
            logoOption: this.getDefaultLogoOption(),
            heading: this.defaultPdfHeading,
            pdfOrientation: this.getDefaultPdfOrientation(),
        };
    }

    public static getLogoOptions(): LogoOption[] {
        return [
            { name: 'timeBuzzer', code: 'default', id: 0 },
            { name: 'Organisation', code: 'custom', id: 1 },
        ];
    }

    public static getDefaultLogoOption(): LogoOptionType | null {
        return null; // see documentation of field "logoOption" for details on null behavior
    }

    public static getPdfOrientationOptions(): SelectOption[] {
        return [
            {
                name: 'pdf_orientation_landscape',
                code: 'landscape',
            },
            {
                name: 'pdf_orientation_portrait',
                code: 'portrait',
            },
        ];
    }

    public static getTranslatedPdfOrientationOptions(translateService: TranslateService): SelectOption[] {
        const options = CloneUtils.cloneData(this.getPdfOrientationOptions());
        options.forEach((option) => {
            option.name = translateService.instant(option.name);
        });
        return options;
    }

    public static getDefaultPdfOrientation(): PdfOrientationType {
        return this.getPdfOrientationOptions()[0].code as PdfOrientationType;
    }

    public static getTranslatedTimeFormatOptions(translateService: TranslateService): SelectOption<TimeFormat>[] {
        return DefaultDateFormatListService.getTranslatedTimeFormats(translateService);
    }

    public static getTimeFormatOptions(): SelectOption[] {
        return DefaultDateFormatListService.getTimeFormats();
    }

    public static getDefaultTimeFormat(): TimeFormat {
        return DefaultDateFormatListService.getDefaultTimeFormat();
    }

    public static getDateFormatOptions(): SelectOption[] {
        return DefaultDateFormatListService.getDateFormats();
    }

    public static getDefaultDateFormat(): string {
        return DefaultDateFormatListService.getDefaultDateFormat();
    }

    public static getDurationFormatOptions(): SelectOption[] {
        return DefaultDateFormatListService.getDurationFormats();
    }

    public static getDefaultDurationFormat(): string {
        return DefaultDateFormatListService.getDefaultDurationFormat();
    }

    public static getRoundingTypeOptions(): SelectOption[] {
        return [
            { code: 'up', name: 'up' },
            { code: 'down', name: 'down' },
            { code: 'nearest', name: 'nearest' },
        ];
    }

    public static getDefaultRoundingType(): RoundingType {
        return this.getRoundingTypeOptions()[0].code as RoundingType;
    }

    public static getRoundingUnitOptions(): SelectOption[] {
        return [
            { code: '1', name: '1 min' },
            { code: '5', name: '5 min' },
            { code: '6', name: '6 min' },
            { code: '10', name: '10 min' },
            { code: '12', name: '12 min' },
            { code: '15', name: '15 min' },
            { code: '20', name: '20 min' },
            { code: '30', name: '30 min' },
            { code: '60', name: '60 min' },
        ];
    }

    public static getDefaultRoundingUnit(): string {
        return this.getRoundingUnitOptions()[0].code;
    }

    public static getDefaultRoundingUnitNumber(): number {
        return Number(this.getRoundingUnitOptions()[0].code);
    }
}
